import { Component, OnDestroy, OnInit, ViewChild, signal } from '@angular/core';
import { Subject, fromEvent } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AdminConfigService } from '../../services/admin-config.service';
import { ToastNotificationComponent } from '../../toast-notification/toast-notification.component';
import { ListItem } from '../../models/ListItem';
import { AcsCommLoadWtDefault, AcsCommodity } from '../models/acs.models';
import { ACS_UOM_OPTIONS, ACS_WILDCARD, ACS_TOAST_SUCCESS_DURATION, ACS_TOAST_ERROR_DURATION } from '../utils/acs.constants';
import { extractErrorMessage, resolveLocCode, scopedLocsToListItems, executeBulkDelete, formatBulkDeleteToast } from '../utils/acs.utils';
import { StateCountryRef, STATES_AND_COUNTRIES } from '../models/acs.reference-data';
import { AcsDropdownState } from '../utils/acs-dropdown-state';
import { AcsDropdownService } from '../utils/acs-dropdown.service';
import { AcsBulkDeleteState } from '../utils/acs-bulk-delete-state';

@Component({
  selector: 'app-acs-variable-weight',
  templateUrl: './acs-variable-weight.component.html',
  styleUrls: ['../acs-shared.css'],
  standalone: false
})
export class AcsVariableWeightComponent implements OnInit, OnDestroy {

  Math = Math;
  readonly ACS_WILDCARD = ACS_WILDCARD;

  private _destroy$ = new Subject<void>();

  @ViewChild('toastNotification') toastNotification!: ToastNotificationComponent;

  // Reference data
  locList: ListItem[] = [];
  commodities: AcsCommodity[] = [];
  uomOptions: string[] = ACS_UOM_OPTIONS;
  statesAndCountries: StateCountryRef[] = STATES_AND_COUNTRIES;

  // Primary loc/group selector
  locDropdown!: AcsDropdownState;
  isAllMode: boolean = false;
  isSaving: boolean = false;

  // Data
  weightDefaults: AcsCommLoadWtDefault[] = [];

  // Toolbar filter dropdowns
  selectedCommodityFilter: string = '';
  commodityFilterSearchText = signal('');

  selectedCountryFilter: string = '';
  countryFilterSearchText = signal('');

  selectedStateFilter: string = '';
  stateFilterSearchText = signal('');

  // Detail panel
  showForm: boolean = false;
  isNewRecord: boolean = false;
  selectedItem: AcsCommLoadWtDefault | null = null;

  // Form fields
  formLocCode: string = '';
  formCommodityCode: string = '';
  formCountryCode: string = '';
  formStateCode: string = '';
  formQuantity: number | null = null;
  formUom: string = '';

  get isWildcardRow(): boolean {
    return this.formLocCode === ACS_WILDCARD;
  }

  // Trade Unit dropdown in form (Add mode)
  formLocSearchText = signal('');

  // Commodity dropdown in form
  commoditySearchText = signal('');

  // Country dropdown in form
  countrySearchText = signal('');

  // State dropdown in form
  stateSearchText = signal('');

  // UOM dropdown in form
  uomSearchText = signal('');

  // Bulk delete
  bulkDelete = new AcsBulkDeleteState<AcsCommLoadWtDefault>(d => d.commLoadWtDefaultId, () => this.selectableDefaults);

  constructor(
    private _adminConfigService: AdminConfigService,
    public dropdowns: AcsDropdownService
  ) {
    this.locDropdown = new AcsDropdownState('vw-loc', this.dropdowns, () => this.locList, '-- Select Trade Unit --');
  }

  ngOnInit(): void {
    this._adminConfigService.getScopedLocs().pipe(takeUntil(this._destroy$)).subscribe({
      next: (result) => this.locList = scopedLocsToListItems(result, false)
    });
    this._adminConfigService.getAllCommodities().pipe(takeUntil(this._destroy$)).subscribe({
      next: (data) => this.commodities = data
    });
    fromEvent(document, 'click').pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.dropdowns.close();
    });
    this.loadDefaults();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  // --- Loc/Group Selector ---

  get formLocDisplay(): string {
    if (!this.formLocCode) return '-- Select Trade Unit --';
    if (this.formLocCode === ACS_WILDCARD) return '* - All';
    const loc: ListItem | undefined = this.locList.find(l => l.value === this.formLocCode);
    return loc ? loc.text : this.formLocCode;
  }

  get filteredFormLocOptions(): ListItem[] {
    const term: string = this.formLocSearchText();
    if (!term) return this.locList;
    const lower: string = term.toLowerCase();
    return this.locList.filter(l => l.text.toLowerCase().includes(lower) || l.value.toLowerCase().includes(lower));
  }

  toggleFormLocDropdown(): void {
    this.dropdowns.toggle('vw-form-loc');
    if (this.dropdowns.isOpen('vw-form-loc')) this.formLocSearchText.set('');
  }

  selectFormLoc(loc: ListItem | null): void {
    this.formLocCode = loc ? loc.value : '';
    this.dropdowns.close();
    this.formLocSearchText.set('');
  }

  selectLocOption(loc: ListItem | null): void {
    this.locDropdown.select(loc);
    this.isAllMode = this.locDropdown.selectedValue === '';
    this.closeForm();
    this.resetFilters();
    this.loadDefaults();
  }

  private getEffectiveLocCode(): string {
    if (!this.locDropdown.selectedValue || this.locDropdown.selectedValue === ACS_WILDCARD) return '';
    return resolveLocCode(this.locDropdown.selectedValue, this.locList);
  }

  // --- Data Loading ---

  loadDefaults(): void {
    this.bulkDelete.selectedIds = new Set();
    const locCode: string = this.getEffectiveLocCode();
    this._adminConfigService.getCommLoadWtDefaults(locCode).pipe(takeUntil(this._destroy$)).subscribe({
      next: (data) => this.weightDefaults = data,
      error: () => this.toastNotification.showToast('Failed to load weight defaults', ACS_TOAST_ERROR_DURATION, 'error')
    });
  }

  // --- Toolbar Filter Dropdowns ---

  get uniqueCommodityCodes(): string[] {
    return [...new Set(this.weightDefaults.map(d => d.commodityCode).filter(Boolean))].sort();
  }

  get uniqueCountryCodes(): string[] {
    return [...new Set(this.weightDefaults.map(d => d.countryCode).filter(Boolean))].sort();
  }

  get uniqueStateCodes(): string[] {
    return [...new Set(this.weightDefaults.map(d => d.stateCode).filter(Boolean))].sort();
  }

  get filteredCommodityFilterOptions(): string[] {
    const term: string = this.commodityFilterSearchText();
    if (!term) return this.uniqueCommodityCodes;
    const lower: string = term.toLowerCase();
    return this.uniqueCommodityCodes.filter(c => c.toLowerCase().includes(lower));
  }

  get filteredCountryFilterOptions(): string[] {
    const term: string = this.countryFilterSearchText();
    if (!term) return this.uniqueCountryCodes;
    const lower: string = term.toLowerCase();
    return this.uniqueCountryCodes.filter(c => c.toLowerCase().includes(lower));
  }

  get filteredStateFilterOptions(): string[] {
    const term: string = this.stateFilterSearchText();
    if (!term) return this.uniqueStateCodes;
    const lower: string = term.toLowerCase();
    return this.uniqueStateCodes.filter(s => s.toLowerCase().includes(lower));
  }

  get commodityFilterDisplay(): string {
    return this.selectedCommodityFilter || '-- All Commodities --';
  }

  get countryFilterDisplay(): string {
    return this.selectedCountryFilter || '-- All Countries --';
  }

  get stateFilterDisplay(): string {
    return this.selectedStateFilter || '-- All States --';
  }

  toggleCommodityFilterDropdown(): void {
    this.dropdowns.toggle('vw-commodity-filter');
    if (this.dropdowns.isOpen('vw-commodity-filter')) this.commodityFilterSearchText.set('');
  }

  toggleCountryFilterDropdown(): void {
    this.dropdowns.toggle('vw-country-filter');
    if (this.dropdowns.isOpen('vw-country-filter')) this.countryFilterSearchText.set('');
  }

  toggleStateFilterDropdown(): void {
    this.dropdowns.toggle('vw-state-filter');
    if (this.dropdowns.isOpen('vw-state-filter')) this.stateFilterSearchText.set('');
  }

  selectCommodityFilterOption(value: string | null): void {
    this.selectedCommodityFilter = value || '';
    this.dropdowns.close();
    this.commodityFilterSearchText.set('');
  }

  selectCountryFilterOption(value: string | null): void {
    this.selectedCountryFilter = value || '';
    this.dropdowns.close();
    this.countryFilterSearchText.set('');
  }

  selectStateFilterOption(value: string | null): void {
    this.selectedStateFilter = value || '';
    this.dropdowns.close();
    this.stateFilterSearchText.set('');
  }

  // --- Filtered Data ---

  get filteredDefaults(): AcsCommLoadWtDefault[] {
    let result: AcsCommLoadWtDefault[] = this.weightDefaults;
    if (this.selectedCommodityFilter) result = result.filter(d => d.commodityCode === this.selectedCommodityFilter);
    if (this.selectedCountryFilter) result = result.filter(d => d.countryCode === this.selectedCountryFilter);
    if (this.selectedStateFilter) result = result.filter(d => d.stateCode === this.selectedStateFilter);
    return result;
  }

  resetFilters(): void {
    this.selectedCommodityFilter = '';
    this.selectedCountryFilter = '';
    this.selectedStateFilter = '';
  }

  // --- Detail Panel ---

  selectItem(item: AcsCommLoadWtDefault): void {
    this.selectedItem = item;
    this.isNewRecord = false;
    this.showForm = true;
    this.formLocCode = item.locCode || '';
    this.formCommodityCode = item.commodityCode || '';
    this.formCountryCode = item.countryCode || '';
    this.formStateCode = item.stateCode || '';
    this.formQuantity = item.defaultWeight != null ? Math.floor(Number(item.defaultWeight)) : null;
    this.formUom = item.uom || '';
  }

  addDefault(): void {
    this.selectedItem = null;
    this.isNewRecord = true;
    this.showForm = true;
    this.formLocCode = this.locDropdown.selectedValue || '';
    this.formCommodityCode = '';
    this.formCountryCode = '';
    this.formStateCode = '';
    this.formQuantity = null;
    this.formUom = '';
  }

  closeForm(): void {
    this.showForm = false;
    this.selectedItem = null;
    this.isNewRecord = false;
  }

  // --- Commodity Dropdown in Form ---

  get filteredCommodities(): AcsCommodity[] {
    const term: string = this.commoditySearchText();
    const allOption: AcsCommodity = { commodityCode: ACS_WILDCARD, description: 'All', shortDescription: 'All', uom: '' };
    const base: AcsCommodity[] = [allOption, ...this.commodities];
    if (!term) return base;
    const lower: string = term.toLowerCase();
    return base.filter(c =>
      c.commodityCode.toLowerCase().includes(lower) ||
      (c.description || '').toLowerCase().includes(lower) ||
      (c.shortDescription || '').toLowerCase().includes(lower)
    );
  }

  get selectedCommodityDisplay(): string {
    if (!this.formCommodityCode) return '-- Select Commodity --';
    if (this.formCommodityCode === ACS_WILDCARD) return '* - All';
    const c: AcsCommodity | undefined = this.commodities.find(c => c.commodityCode === this.formCommodityCode);
    return c ? `${c.commodityCode} - ${c.description || c.shortDescription || ''}` : this.formCommodityCode;
  }

  toggleCommodityDropdown(): void {
    this.dropdowns.toggle('vw-form-commodity');
    if (this.dropdowns.isOpen('vw-form-commodity')) this.commoditySearchText.set('');
  }

  selectCommodity(c: AcsCommodity): void {
    this.formCommodityCode = c ? c.commodityCode : '';
    this.dropdowns.close();
    this.commoditySearchText.set('');
    if (c && c.commodityCode !== ACS_WILDCARD && c.uom) {
      this.formUom = c.uom;
    } else if (c && c.commodityCode === ACS_WILDCARD) {
      this.formUom = '';
    }
  }

  get isUomReadOnly(): boolean {
    return !!this.formCommodityCode && this.formCommodityCode !== ACS_WILDCARD;
  }

  // --- Country Dropdown ---

  get countries(): { code: string; name: string }[] {
    const unique = new Map<string, string>();
    for (const sc of this.statesAndCountries) {
      if (!unique.has(sc.countryCode)) {
        unique.set(sc.countryCode, sc.countryName);
      }
    }
    return Array.from(unique, ([code, name]) => ({ code, name }));
  }

  get filteredCountryOptions(): { code: string; name: string }[] {
    const term: string = this.countrySearchText();
    if (!term) return this.countries;
    const lower: string = term.toLowerCase();
    return this.countries.filter(c => c.code.toLowerCase().includes(lower) || c.name.toLowerCase().includes(lower));
  }

  get selectedCountryDisplay(): string {
    if (!this.formCountryCode) return '-- Select Country --';
    if (this.formCountryCode === ACS_WILDCARD) return '* - All';
    const c = this.countries.find(c => c.code === this.formCountryCode);
    return c ? `${c.code} - ${c.name}` : this.formCountryCode;
  }

  toggleCountryDropdown(): void {
    this.dropdowns.toggle('vw-form-country');
    if (this.dropdowns.isOpen('vw-form-country')) this.countrySearchText.set('');
  }

  selectCountry(c: { code: string; name: string } | null): void {
    this.formCountryCode = c ? c.code : '';
    this.dropdowns.close();
    this.countrySearchText.set('');
    if (this.formCountryCode === ACS_WILDCARD) {
      this.formStateCode = ACS_WILDCARD;
    } else {
      this.formStateCode = '';
    }
  }

  // --- State Dropdown ---

  get filteredStates(): StateCountryRef[] {
    if (!this.formCountryCode || this.formCountryCode === ACS_WILDCARD) {
      return this.statesAndCountries;
    }
    return this.statesAndCountries.filter(s => s.countryCode === this.formCountryCode || s.stateCode === ACS_WILDCARD);
  }

  get filteredStateOptions(): StateCountryRef[] {
    const base: StateCountryRef[] = this.filteredStates;
    const term: string = this.stateSearchText();
    if (!term) return base;
    const lower: string = term.toLowerCase();
    return base.filter(s => s.stateCode.toLowerCase().includes(lower) || s.stateName.toLowerCase().includes(lower));
  }

  get selectedStateDisplay(): string {
    if (!this.formStateCode) return '-- Select State --';
    if (this.formStateCode === ACS_WILDCARD) return '* - All';
    const s = this.statesAndCountries.find(s => s.stateCode === this.formStateCode);
    return s ? `${s.stateCode} - ${s.stateName}` : this.formStateCode;
  }

  toggleStateDropdown(): void {
    this.dropdowns.toggle('vw-form-state');
    if (this.dropdowns.isOpen('vw-form-state')) this.stateSearchText.set('');
  }

  selectState(s: StateCountryRef | null): void {
    this.formStateCode = s ? s.stateCode : '';
    this.dropdowns.close();
    this.stateSearchText.set('');
  }

  // --- UOM Dropdown ---

  get filteredUomList(): string[] {
    const term: string = this.uomSearchText();
    if (!term) return this.uomOptions;
    const lower: string = term.toLowerCase();
    return this.uomOptions.filter(u => u.toLowerCase().includes(lower));
  }

  get selectedUomDisplay(): string {
    if (!this.formUom) return '-- Select UOM --';
    return this.formUom;
  }

  toggleUomDropdown(): void {
    this.dropdowns.toggle('vw-form-uom');
    if (this.dropdowns.isOpen('vw-form-uom')) this.uomSearchText.set('');
  }

  selectUom(u: string | null): void {
    this.formUom = u || '';
    this.dropdowns.close();
    this.uomSearchText.set('');
  }

  // --- Save ---

  saveDefault(): void {
    if (!this.formLocCode || !this.formCommodityCode || !this.formStateCode || this.formQuantity === null) {
      this.toastNotification.showToast('Please fill in all required fields', ACS_TOAST_SUCCESS_DURATION, 'error');
      return;
    }

    const dto: Partial<AcsCommLoadWtDefault> = {
      commLoadWtDefaultId: this.isNewRecord ? 0 : (this.selectedItem?.commLoadWtDefaultId || 0),
      locCode: resolveLocCode(this.formLocCode, this.locList),
      commodityCode: this.formCommodityCode,
      stateCode: this.formStateCode,
      countryCode: this.formCountryCode,
      defaultWeight: Math.floor(this.formQuantity),
      uom: this.formUom
    };

    this.isSaving = true;
    this._adminConfigService.saveCommLoadWtDefault(dto).pipe(takeUntil(this._destroy$)).subscribe({
      next: () => {
        this.isSaving = false;
        this.toastNotification.showToast('Weight default saved', ACS_TOAST_SUCCESS_DURATION, 'success');
        this.closeForm();
        this.loadDefaults();
      },
      error: (err) => {
        this.isSaving = false;
        const msg: string = extractErrorMessage(err, 'Failed to save weight default');
        this.toastNotification.showToast(msg, ACS_TOAST_ERROR_DURATION, 'error');
      }
    });
  }

  // --- Delete ---

  get selectableDefaults(): AcsCommLoadWtDefault[] {
    return this.filteredDefaults.filter(d => d.locCode !== ACS_WILDCARD);
  }

  confirmBulkDelete(): void {
    this.bulkDelete.isDeleting = true;
    const ids: number[] = [...this.bulkDelete.selectedIds];
    executeBulkDelete(ids, (id: number) => this._adminConfigService.deleteCommLoadWtDefault(id))
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (result) => {
          const toast = formatBulkDeleteToast(result, 'weight default');
          this.toastNotification.showToast(toast.message, toast.type === 'success' ? ACS_TOAST_SUCCESS_DURATION : ACS_TOAST_ERROR_DURATION, toast.type);
          if (this.selectedItem && ids.includes(this.selectedItem.commLoadWtDefaultId)) {
            this.closeForm();
          }
          this.bulkDelete.reset();
          this.loadDefaults();
        }
      });
  }

  // --- Display Helpers ---

  getLocDisplay(locCode: string): string {
    if (!locCode || locCode === ACS_WILDCARD) return locCode;
    const loc: ListItem | undefined = this.locList.find(l => l.value === locCode || l.text.split(' - ')[0] === locCode);
    return loc ? loc.text : locCode;
  }

  getCommodityDisplay(commodityCode: string): string {
    if (!commodityCode || commodityCode === ACS_WILDCARD) return commodityCode;
    const comm: AcsCommodity | undefined = this.commodities.find(c => c.commodityCode === commodityCode);
    if (!comm) return commodityCode;
    const desc: string = comm.description || comm.shortDescription || '';
    return desc ? `${commodityCode} - ${desc}` : commodityCode;
  }

  formatQuantity(value: any): string {
    if (value === null || value === undefined) return '';
    const num: number = Number(value);
    if (isNaN(num)) return String(value);
    return Math.floor(num).toLocaleString();
  }
}
